import axios from 'axios';

/**
 * Common download request API that preserves response headers
 * Specifically designed for file downloads (PDF, Excel, etc.)
 */
const downloadRequestApi = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
  headers: {
    accept: '*/*',
    language: 'en',
    'Access-Control-Allow-Origin': '*',
  },
});

// Request interceptor to add authorization token
downloadRequestApi.interceptors.request.use(
  (config) => {
    const token =
      JSON.parse(localStorage.getItem('revive_admin__token')) || null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - IMPORTANT: Returns full response object to preserve headers
downloadRequestApi.interceptors.response.use(
  (response) => {
    // For download requests, we need the full response object including headers
    // Do NOT return just response.data like the regular requestApi
    return Promise.resolve(response);
  },
  async (error) => {
    if (error?.response?.status === 422) {
      return await Promise.reject({
        status: 422,
        message: error?.response?.data?.message || 'Validation failed',
      });
    } else if (error?.response?.status === 403) {
      localStorage.clear();
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      return Promise.reject({
        status: 403,
        message: error?.response?.data?.error?.message || 'Forbidden access',
      });
    } else if (error?.response?.status === 400) {
      return Promise.reject({
        status: 400,
        message:
          error?.response?.data?.error?.message || 'Incorrect input value',
      });
    } else if (error?.response?.status === 405) {
      return Promise.reject({
        status: 405,
        message: error?.response?.data?.message || 'Incorrect input value',
      });
    } else if (error?.response?.status === 401) {
      localStorage.clear();
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      return Promise.reject({
        status: 401,
        message:
          error?.response?.data?.message ||
          'Your Session is expired. Please Login again',
      });
    } else {
      let message = 'Something went to wrong.';
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          message = json?.error?.message || json?.message || message;
        } else {
          message = error?.response?.data?.error?.message || message;
        }
      } catch (_) {}
      return Promise.reject({
        message,
        status: error?.response?.status || 500,
      });
    }
  },
);

/**
 * Download file with proper filename handling
 * @param {string} endpoint - API endpoint
 * @param {object} params - Query parameters
 * @param {string} defaultFilename - Fallback filename if header not present
 * @param {string} mimeType - MIME type for the blob (default: 'application/pdf')
 * @param {string} extension - File extension to append (default: '.pdf')
 * @param {function} onSuccess - Success callback
 * @param {function} onError - Error callback
 */
export const downloadFile = async (
  endpoint,
  params = {},
  defaultFilename = 'download',
  mimeType = 'application/pdf',
  extension = '.pdf',
  onSuccess = null,
  onError = null,
) => {
  try {
    const response = await downloadRequestApi.get(endpoint, {
      params,
      responseType: 'blob',
      headers: {
        'Access-Control-Expose-Headers': 'X-Filename, Content-Disposition',
      },
    });

    // Try multiple ways to get filename
    const xFilename = response.headers['x-filename'];
    const XFilename = response.headers['X-Filename'];
    const contentDisposition = response.headers['content-disposition'];

    // Extract filename from Content-Disposition if available
    let filenameFromDisposition = null;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (filenameMatch && filenameMatch[1]) {
        filenameFromDisposition = filenameMatch[1].replace(/['"]/g, '');
        // Remove .pdf extension if present since we add it later
        filenameFromDisposition = filenameFromDisposition.replace(
          /\.pdf$/i,
          '',
        );
      }
    }

    // Priority: X-Filename header > x-filename header > Content-Disposition > default
    const filename = XFilename || xFilename || filenameFromDisposition;
    let finalFilename;

    if (extension === '.zip') {
      finalFilename = defaultFilename;
      // Remove any existing extension from the filename
      finalFilename = finalFilename.split('.')[0];
    } else {
      finalFilename = filename || defaultFilename;
    }

    // Create blob and download
    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = `${finalFilename}${extension}`;
    a.click();

    window.URL.revokeObjectURL(url);

    if (typeof onSuccess === 'function') {
      onSuccess(finalFilename);
    }

    return { success: true, filename: finalFilename };
  } catch (error) {
    if (typeof onError === 'function') {
      onError(error);
    }

    throw error;
  }
};

export default downloadRequestApi;
